// frontend/src/hooks/useEntryStatus.js
//
// The one read the entry screens decide on (`GET /api/store/entry/`,
// backend/shopify_app/views/saas.py `store_entry`): the sign-in claim's
// question, the selected store's install setup step, its billing status and
// its sync progress. `components/entry/entryScreen.js` orders the screens off
// it; the Plans page and the plan-change banner read its billing part through
// `contexts/BillingStatusContext`.
//
// A part is null where it does not apply: setup, billing and sync for a store
// that is not a Shopify store or for no store, and all three while the claim
// is pending or failed — the question comes before the store it may hide. A
// part the backend could not read is named in the payload's `failed`, and
// fails closed into its own retry screen.
//
// Policy:
//
//   200        → `checked`, and each part as the payload says. The sync
//                payload seeds the TanStack sync-status query
//                (`hooks/useSyncStatus`), which polls on from there.
//   401 / 403  → `sessionLost`; the session died between the auth check and
//                this read, and sending the merchant to the paywall instead
//                of to login would invite a duplicate Shopify subscription.
//   any other  → FAIL CLOSED: the whole read failed, and the claim — the
//                first part — reads as failed.
//
// The one non-verdict is a request the browser killed because the user is
// leaving (`utils/pageTeardown`); a restored document re-arms the read.

import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '../services/SessionApiService';
import { syncStatus as syncStatusKey } from '../queries/keys';
import { apiErrorFrom } from '../utils/parseApiError';
import { isPageBeingReplaced, onPageRestored } from '../utils/pageTeardown';

const ENTRY_URL = '/api/store/entry/';
const CLAIM_URL = '/api/store/claim/';
const SETUP_URL = '/api/store/setup/';
const BILLING_STATUS_URL = '/api/billing/status/';

const NOT_READ = {
  claim: { failed: false, question: null }, setup: null, billing: null, sync: null, readError: null,
};
const readFailed = (readError) => ({
  claim: { failed: true, question: null }, setup: null, billing: null, sync: null, readError,
});

/**
 * The entry view model of a `store_entry` payload.
 *
 *   claim    `{failed, question}` — `question` is the pending payload or null.
 *   setup    null where there is no setup step (no Shopify store), else
 *            `{failed, step}` — `step` is the pending payload (the store's
 *            stored values the form pre-fills from) or null once answered.
 *   billing  null where billing does not apply, else `{status}`; `status`
 *            null is a failed read (the paywall's retry card).
 *   sync     null where there is no import to read, else `{failed}`; the
 *            payload itself lives in the sync-status query.
 *   readError  null when the read answered. When the whole read failed, the
 *            request's error: the claim reads as failed, and the retry screen
 *            names the next step for that cause.
 */
function entryFrom(payload) {
  const failed = new Set(payload.failed);
  return {
    claim: { failed: failed.has('claim'), question: payload.claim?.pending ? payload.claim : null },
    setup: failed.has('setup')
      ? { failed: true, step: null }
      : payload.setup && { failed: false, step: payload.setup.pending ? payload.setup : null },
    billing: failed.has('billing') ? { status: null } : payload.billing && { status: payload.billing },
    sync: failed.has('sync') ? { failed: true } : payload.sync && { failed: false },
    readError: null,
  };
}

/**
 * @param {Object} options
 * @param {boolean} options.applies  false until the session is authenticated
 *   and its stores are loaded — nothing is fetched and `checked` stays false.
 * @param {string|null} options.storeId  identity of the selected store, the
 *   `X-Store-ID` the read goes out with. An answer for another store is not
 *   an answer: `checked` is false until this store's read lands.
 */
export function useEntryStatus({ applies, storeId }) {
  const queryClient = useQueryClient();
  const [answer, setAnswer] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [sessionLost, setSessionLost] = useState(false);

  useEffect(() => {
    if (!applies) return undefined;

    let cancelled = false;
    async function read() {
      try {
        const response = await apiRequest(ENTRY_URL);
        if (response.status === 401 || response.status === 403) {
          if (!cancelled) setSessionLost(true);
          return;
        }
        if (!response.ok) {
          const error = await apiErrorFrom(response, `GET ${ENTRY_URL}`);
          if (!cancelled) setAnswer({ storeId, entry: readFailed(error) });
          return;
        }
        const payload = await response.json();
        if (cancelled) return;
        if (payload.sync) queryClient.setQueryData(syncStatusKey.all(storeId), payload.sync);
        setAnswer({ storeId, entry: entryFrom(payload) });
      } catch (err) {
        if (isPageBeingReplaced()) return;
        console.error('Entry read failed:', err.message);
        if (!cancelled) setAnswer({ storeId, entry: readFailed(err) });
      }
    }
    read();
    return () => { cancelled = true; };
  }, [applies, storeId, attempt, queryClient]);

  // Back/forward restore. However long the freeze lasted, the merchant's
  // access and their import may have changed: re-read, with the current
  // answer on screen until the fresh one lands.
  useEffect(() => onPageRestored(() => setAttempt((n) => n + 1)), []);

  // Re-read from scratch: `checked` goes back to false, so the layout shows
  // its loading state. Every entry retry screen's button, and what a stale
  // claim answer (`answer_mismatch`, `store_already_claimed`) falls back to.
  const retry = useCallback(() => {
    setAnswer(null);
    setAttempt((n) => n + 1);
  }, []);

  // Re-read billing alone while the app stays mounted: the fresh payload
  // replaces the old one when it lands. For a billing change the merchant
  // just made inside the app (a plan switch they withdrew). A failed re-read
  // still fails closed.
  const refreshBilling = useCallback(async () => {
    const setStatus = (status) => setAnswer((prev) => prev && {
      ...prev, entry: { ...prev.entry, billing: { status } },
    });
    try {
      const response = await apiRequest(BILLING_STATUS_URL);
      if (response.status === 401 || response.status === 403) {
        setSessionLost(true);
      } else {
        setStatus(response.ok ? await response.json() : null);
      }
    } catch (err) {
      if (isPageBeingReplaced()) return;
      console.error('Billing status re-read failed:', err.message);
      setStatus(null);
    }
  }, []);

  /**
   * Post the merchant's answer to the claim's question. Resolves the
   * `{selected_store}` payload; the caller restarts into that store. Rejects
   * with the `ApiError` for the refusal — `code` is the structured error
   * code, or `null` when the body had none.
   */
  const answerClaim = useCallback(async (body) => {
    const response = await apiRequest(CLAIM_URL, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!response.ok) throw await apiErrorFrom(response, `POST ${CLAIM_URL}`);
    return response.json();
  }, []);

  /**
   * Post the merchant's answer to the install setup step — the `planning`,
   * `sourcing` and `catalog` blocks (`shopify_app.views.saas.store_setup`).
   * Resolves the setup part it answers with; the caller re-reads the entry.
   * Rejects with the `ApiError` for the refusal: `body.errors` names each
   * refused field, `code` the structured refusal (`rebuild_unavailable`).
   */
  const answerSetup = useCallback(async (body) => {
    const response = await apiRequest(SETUP_URL, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!response.ok) throw await apiErrorFrom(response, `POST ${SETUP_URL}`);
    return response.json();
  }, []);

  const current = answer !== null && answer.storeId === storeId ? answer.entry : null;
  return {
    checked: current !== null,
    ...(current ?? NOT_READ),
    sessionLost,
    retry,
    refreshBilling,
    answerClaim,
    answerSetup,
  };
}

export default useEntryStatus;
